import { CSSProperties, forwardRef, ReactNode, useMemo } from "react";
import classNames from "./Button.module.css";

const sizes = [
  "x-small",
  "small",
  "medium",
  "large",
  "landing",
  "landing-header",
] as const;

export type Size = (typeof sizes)[number];

const colors = [
  "primary",
  "transparent",
  "black",
  "gray",
  "dark-gray",
  "white",
  "red",
  "orange",
  "blue",
] as const;

export type Color = (typeof colors)[number];

const Button = forwardRef(
  (
    props: {
      children: ReactNode | ReactNode[];
      size?: Size;
      color?: Color;
      backgroundColor?: Color;
      borderColor?: Color;
      className?: string;
      style?: CSSProperties;
      onClick?: (e?: React.MouseEvent) => void;
      disabled?: boolean;
      type?: "button" | "submit";
    },
    forwardedRef: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const sizeClassName = useMemo(() => {
      switch (props.size) {
        case "x-small":
          return classNames.ExtraSmall;
        case "small":
          return classNames.Small;
        case "large":
          return classNames.Large;
        case "landing":
          return classNames.Landing;
        case "landing-header":
          return classNames.LandingHeader;
        default:
          return classNames.Small;
      }
    }, [props.size]);

    const colorClassName = useMemo(() => {
      if (props.disabled) {
        return "";
      }

      switch (props.color) {
        case "primary":
          return classNames.Primary;
        case "black":
          return classNames.Black;
        case "gray":
          return classNames.Gray;
        case "dark-gray":
          return classNames.DarkGray;
        case "white":
          return classNames.White;
        case "red":
          return classNames.Red;
        case "orange":
          return classNames.Orange;
        default:
          return classNames.White;
      }
    }, [props.disabled, props.color]);

    const backgroundColorClassName = useMemo(() => {
      if (props.disabled) {
        return "";
      }

      switch (props.backgroundColor) {
        case "primary":
          return classNames.BackgroundPrimary;
        case "transparent":
          return classNames.BackgroundTransparent;
        case "black":
          return classNames.BackgroundBlack;
        case "gray":
          return classNames.BackgroundGray;
        case "dark-gray":
          return classNames.BackgroundDarkGray;
        case "white":
          return classNames.BackgroundWhite;
        case "red":
          return classNames.BackgroundRed;
        case "orange":
          return classNames.BackgroundOrange;
        case "blue":
          return classNames.BackgroundBlue;
        default:
          return classNames.BackgroundPrimary;
      }
    }, [props.disabled, props.backgroundColor]);

    const borderColorClassName = useMemo(() => {
      if (props.disabled) {
        return "";
      }

      switch (props.borderColor) {
        case "primary":
          return classNames.BorderPrimary;
        case "white":
          return classNames.BorderWhite;
        case "transparent":
          return classNames.BorderTransparent;
        case "black":
          return classNames.BorderBlack;
        case "gray":
          return classNames.BorderGray;
        case "dark-gray":
          return classNames.BorderDarkGray;
        case "red":
          return classNames.BorderRed;
        case "orange":
          return classNames.BorderOrange;
        case "blue":
          return classNames.BorderBlue;
        default:
          return classNames.BorderPrimary;
      }
    }, [props.disabled, props.borderColor]);

    return (
      <button
        type={props.type || "button"}
        className={`${sizeClassName} ${colorClassName} ${backgroundColorClassName} ${borderColorClassName} ${
          props.className ?? ""
        }`}
        style={props.style}
        onClick={!props.disabled ? props.onClick : undefined}
        disabled={props.disabled}
        ref={forwardedRef}
      >
        {props.children}
      </button>
    );
  }
);

export default Button;
